import {ChemistryPreferences} from 'chemistry/ChemistryConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ListingRendererType, ListingTypes} from 'nav/expanded/ListingRendererConstants';
import preferences from 'Preferences';
import {
  ChemicalStructureFilterFormat,
  ChemicalStructureSearchOperator,
  ChemicalStructureSearchParams,
  FilterKey,
  isChemicalStructureFilter,
  Operator,
  SearchParams,
  SearchResults,
} from 'search/SearchConstants';
import {mergeFiltersToSearchParams} from 'search/SearchUtil';
import APIUtil from 'util/APIUtil';
import {MakeFieldsRequired} from 'util/Types';

import {ChemSearchFolderItemListingExtras} from './ChemistryConstants';

/**
 * Get the unique mol query from the search params, if it exists, otherwise null.
 */
export function extractMolQuery(searchParams: SearchParams): string | null {
  for (const filter of searchParams.filters) {
    if (isChemicalStructureFilter(filter) && filter.format === ChemicalStructureFilterFormat.MOL) {
      return filter.value;
    }
  }
  return null;
}

export type CompleteChemSearchParams = MakeFieldsRequired<
  ChemicalStructureSearchParams,
  'chemicalStructureQuery'
>;

export function isCompleteChemSearchParams(
  chemSearchParams: ChemicalStructureSearchParams
): chemSearchParams is CompleteChemSearchParams {
  return chemSearchParams.chemicalStructureQuery != null;
}

/**
 * Return an updated copy of the given search params that performs the given chem search.
 */
export function updateChemSearchParams(
  searchParams: SearchParams,
  chemSearchParams: CompleteChemSearchParams
): SearchParams {
  const {chemicalStructureQuery, chemSearchOperator, minSimilarity} = chemSearchParams;
  return mergeFiltersToSearchParams(searchParams, [
    {
      key: FilterKey.TYPES,
      operator: Operator.IS_ONE_OF,
      value: [ModelTypeConstants.MOLECULE],
    },
    {
      key: FilterKey.CHEMICAL_STRUCTURE,
      operator: chemSearchOperator,
      format: ChemicalStructureFilterFormat.MOL,
      value: chemicalStructureQuery,
      minSimilarity,
    },
  ]);
}
export interface ToggleHighlightParams {
  showToggleHighlightOption: boolean;
  shouldHighlightSubstructure: boolean;
  setShouldHighlightSubstructure: (shouldHighlightSubstructure: boolean) => void;
}

export function getChemSearchOperator(searchParams: SearchParams): Operator | null {
  for (const filter of searchParams.filters) {
    if (isChemicalStructureFilter(filter)) {
      return filter.operator;
    }
  }
  return null;
}

export function isSubstructureSearch(searchParams: SearchParams): boolean {
  return getChemSearchOperator(searchParams) === Operator.SUBSTRUCTURE;
}

export function shouldShowChemSearchColumnsFirst(
  items: Array<ListingTypes[ListingRendererType.FOLDER_ITEM]>
): boolean {
  return items.every((item) => APIUtil.isMolecule(item.id));
}

export function isSimilaritySearch(searchParams: SearchParams): boolean {
  return getChemSearchOperator(searchParams) === Operator.SIMILARITY;
}

export function isChemSearch(searchParams: SearchParams): boolean {
  return isSubstructureSearch(searchParams) || isSimilaritySearch(searchParams);
}

export function getChemSearchFolderItemListingExtras(
  searchParams: SearchParams,
  chemicalStructureQuery: string | null,
  shouldHighlightSubstructure: boolean,
  searchResults: SearchResults | null
): ChemSearchFolderItemListingExtras | null {
  return isChemSearch(searchParams)
    ? {
        substructureQuery: isSubstructureSearch(searchParams) ? chemicalStructureQuery : null,
        shouldHighlightSubstructure,
        searchScoresById: searchResults?.scoresById,
      }
    : null;
}

export function getSimilarityOptionLabel(minSimilarity: number): string {
  return `>=${minSimilarity * 100}%`;
}

export function getDefaultChemSearchOperator(searchParams: SearchParams): ChemicalStructureSearchOperator {
  if (isSimilaritySearch(searchParams)) {
    return Operator.SIMILARITY;
  } else if (isSubstructureSearch(searchParams)) {
    return Operator.SUBSTRUCTURE;
  } else {
    return preferences.get(ChemistryPreferences.CHEM_SEARCH_TYPE) ?? Operator.SUBSTRUCTURE;
  }
}

export function getChemSearchChipLabel(searchParams: SearchParams): string {
  let chipText = '';
  if (isSubstructureSearch(searchParams)) {
    chipText = 'Substructure Search';
  }
  if (isSimilaritySearch(searchParams)) {
    chipText = 'Similarity Search';
  }
  return chipText;
}
